import React, { useRef, useState, useEffect } from 'react';
import moment, { Moment } from 'moment-timezone';
import { track, TrackingEvent, TrackingEventProperty } from 'common/charts/visualization/line-chart/annotations/tracking';
import { getReportName } from 'common/report/util';
import { ChangeEvent, MenuChangeEvent } from 'common/components/select/types';
import { Permission } from 'common/report/constants';
import { ButtonReact } from 'common/components/button';
import { ButtonBarReact } from 'common/components/button-bar';
import { ButtonTheme } from 'common/components/button/constants';
import { CalendarReact } from 'common/components/calendar';
import { SelectReact } from 'common/components/select';
import { TooltipReact } from 'common/components/tooltip';
import { DisplayType, Placement, PositioningStrategy } from 'common/components/tooltip/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useTranslation } from 'common/i18n/hooks';
import { AnnotationEvent } from 'common/charts/visualization/line-chart/annotations/type';
import styles from './index.module.styl';
const timeItems = ["12:00am", "1:00am", "2:00am", "3:00am", "4:00am", "5:00am", "6:00am", "7:00am", "8:00am", "9:00am", "10:00am", "11:00am", "12:00pm", "1:00pm", "2:00pm", "3:00pm", "4:00pm", "5:00pm", "6:00pm", "7:00pm", "8:00pm", "9:00pm", "10:00pm", "11:00pm"].map(hour => ({
  label: hour
}));
type Props = {
  description?: string;
  addAnnotationText?: string;
  date: string;
  utcOffset: number;
  onCreate: (annotation: AnnotationEvent) => void;
  onClose: () => void;
};
export function CreateAnnotationForm(_ref: Props) {
  var _currentProject$get$v;
  let {
    description = "",
    addAnnotationText,
    date,
    utcOffset,
    onCreate,
    onClose
  }: Props = _ref;
  const currentProject = useCurrentProject();
  const [currentDate, setCurrentDate] = useState<Moment>(moment(date, "MMM D, YYYY HH:mm:ss"));
  const [dateInputFocused, setDateInputFocused] = useState(false);
  const [localDescription, setDescription] = useState(description);
  const [invalidDescription, setInvalidDescription] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tmpDate, setTmpDate] = useState<Moment>(moment(date, "MMM D, YYYY HH:mm:ss"));
  const {
    print,
    t
  } = useTranslation();
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    requestAnimationFrame(() => {
      if (descriptionRef.current) {
        descriptionRef.current.focus();
      }
    });
  }, [descriptionRef.current]);
  const hasValidUserPermissions = (_currentProject$get$v = currentProject.get().value) === null || _currentProject$get$v === void 0 ? void 0 : _currentProject$get$v.hasPermission(Permission.WriteAnnotations);
  const handleSelectDateButtonClicked = () => {
    setCurrentDate(currentDate.clone().year(tmpDate.year()).month(tmpDate.month()).date(tmpDate.date()));
    setDateInputFocused(false);
  };
  const handleDateSelectChange = (_ref2: MenuChangeEvent) => {
    let {
      detail
    }: MenuChangeEvent = _ref2;
    setDateInputFocused(detail.isOpen);
  };
  const handleCancelCreation = () => {
    onClose();
  };
  const handleSaveAnnotation = () => {
    if (!localDescription) {
      setInvalidDescription(true);
      return;
    }
    const annotationProjectTime = utcOffset ? moment(currentDate).clone().utcOffset(utcOffset, true) : currentDate;
    setSaving(true);
    if (hasValidUserPermissions) {
      onCreate({
        annotation: {
          date: annotationProjectTime.format("YYYY-MM-DD HH:mm:ss"),
          description: localDescription
        }
      });
      track(TrackingEvent.Add, {
        [TrackingEventProperty.AnnotationText]: localDescription,
        [TrackingEventProperty.AnnotationTimestamp]: currentDate.format("YYYY-MM-DD HH:mm:ss"),
        [TrackingEventProperty.ReportName]: getReportName()
      });
    }
  };
  return <div className={styles.container} elref={"createAnnotationForm"} data-sentry-component="CreateAnnotationForm" data-sentry-source-file="index.tsx">
      <div className={styles.header}>
        <div className={"create-annotation-label"}>{addAnnotationText || t("chart-actions.add-annotation")}</div>
      </div>
      <div className={styles.dateInputRow}>
        <div className={styles.dateContainer}>
          <SelectReact className={styles.dateSelect} open={dateInputFocused} selectedOption={currentDate.format("MMM D, YYYY")} onMenuChange={handleDateSelectChange} data-sentry-element="SelectReact" data-sentry-source-file="index.tsx">
            <div className={styles.datePopupContent} slot={"body"}>
              <CalendarReact selectedDate={tmpDate.format("YYYY-MM-DD")} onSelectDate={ev => {
              setTmpDate(moment((ev.detail as {
                date: string;
              }).date));
            }} data-sentry-element="CalendarReact" data-sentry-source-file="index.tsx" />
            </div>
            <div className={"create-annotation-date-popup-footer"} slot={"footer"}>
              <ButtonBarReact className={"mp-button-bar-primary"} slot={"footer"} onClick={handleSelectDateButtonClicked} data-sentry-element="ButtonBarReact" data-sentry-source-file="index.tsx">
                Select date
              </ButtonBarReact>
            </div>
          </SelectReact>
          <div className={styles.atLabel}>at</div>
        </div>
        <div className={styles.timeContainer}>
          <SelectReact className={styles.timeSelect} disableSearch sections={[{
          items: timeItems
        }]} selectedOption={currentDate.format("hh:mma")} onChange={ev => {
          const newDate = moment(print((ev as ChangeEvent).detail.item.label), "h:mma");
          setCurrentDate(currentDate.clone().hour(newDate.hour()).minute(newDate.minute()));
        }} data-sentry-element="SelectReact" data-sentry-source-file="index.tsx" />
        </div>
      </div>
      <div className={styles.description}>
        <textarea ref={descriptionRef} maxLength={512} value={localDescription} placeholder={"Add a description"} onChange={ev => {
        setDescription(ev.target.value);
        setInvalidDescription(false);
      }} />
        {invalidDescription ? <TooltipReact alwaysShow placement={Placement.Bottom} type={DisplayType.Error}>
            Please enter a description
          </TooltipReact> : null}
      </div>
      <div className={styles.actionsRow}>
        <ButtonReact className={styles.cancelButton} theme={ButtonTheme.Tertiary} onClick={handleCancelCreation} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
          Cancel
        </ButtonReact>
        <div className={"save-annotations-button"}>
          <ButtonReact disabled={saving || !hasValidUserPermissions} theme={ButtonTheme.Primary} onClick={handleSaveAnnotation} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
            Save
          </ButtonReact>
          {!hasValidUserPermissions ? <TooltipReact alwaysShow={false} placement={Placement.RightEnd} positionStrategy={PositioningStrategy.Absolute} type={DisplayType.Default}>
              You don't have the requisite permissions to save an annotation. Please contact your project owner.
            </TooltipReact> : null}
        </div>
      </div>
    </div>;
}